/**
 * Itens do pedido além do produto principal — order bumps marcados no checkout
 * e ofertas aceitas depois da compra (upsell/downsell one-click) — para o
 * obrigado listar o pedido completo. Fica no sessionStorage da mesma aba,
 * por caminho do obrigado E comprovante da compra principal — duas compras
 * seguidas na mesma aba não misturam ofertas. Nunca na URL (o link do
 * obrigado é compartilhável).
 */
export interface IOrderExtra {
	kind: 'orderBump' | 'upsell' | 'downsell';
	title: string;
	amount: number | null;
	reference?: string;
}

const key = (thanksPath: string, receipt: string | null) =>
	`checkout-order-extras:${thanksPath}:${receipt ?? ''}`;

export const readOrderExtras = (
	thanksPath: string,
	receipt: string | null
): IOrderExtra[] => {
	try {
		const parsed = JSON.parse(
			sessionStorage.getItem(key(thanksPath, receipt)) || '[]'
		);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

export const addOrderExtra = (
	thanksPath: string,
	receipt: string | null,
	extra: IOrderExtra
) => {
	try {
		const current = readOrderExtras(thanksPath, receipt).filter(
			// Mesmo item gravado duas vezes (retry/duplo clique) conta uma vez.
			// Order bump não tem comprovante próprio: vale o tipo + título.
			(item) =>
				extra.reference
					? item.reference !== extra.reference
					: !(item.kind === extra.kind && item.title === extra.title)
		);
		sessionStorage.setItem(
			key(thanksPath, receipt),
			JSON.stringify([...current, extra])
		);
	} catch {
		// sem storage: o obrigado só não lista a oferta (a compra já foi feita)
	}
};

/**
 * Valores do "Seu pedido". `purchaseValue` é o `v` do redirect: produto +
 * order bumps (é o valor dos pixels de compra). O produto sozinho é ele menos
 * os bumps; o total soma as ofertas aceitas depois (upsell/downsell), que são
 * cobranças à parte.
 */
export const orderSummary = (
	purchaseValue: number | null,
	extras: IOrderExtra[]
): { mainValue: number | null; total: number | null } => {
	if (purchaseValue === null) return { mainValue: null, total: null };
	const bumps = extras.filter((extra) => extra.kind === 'orderBump');
	const mainValue = bumps.every((bump) => bump.amount !== null)
		? Math.round(
				(purchaseValue -
					bumps.reduce((sum, bump) => sum + Number(bump.amount), 0)) *
					100
		  ) / 100
		: null;
	const offers = extras.filter((extra) => extra.kind !== 'orderBump');
	const total = offers.every((offer) => offer.amount !== null)
		? Math.round(
				offers.reduce(
					(sum, offer) => sum + Number(offer.amount),
					purchaseValue
				) * 100
		  ) / 100
		: null;
	return { mainValue, total };
};
